import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Chip,
  Typography,
  CardActionArea,
  LinearProgress,
  IconButton,
  Avatar as MUIAvatar,
  alpha,
} from '@mui/material';
import DifficultyLevelChip from './difficulty-level-chip';
import { LockRounded, PlayArrowRounded } from '@mui/icons-material';
import { ICourseData } from '../../types/course-data';
import Avatar from './avatar';
import { SkillLevel } from '../../types/skill-level';
import { adaptCourseTypeText } from 'src/shared/utils/adapt-course-type-text';

export const CourseCard = ({
  cardBackgroundColor,
  imageSrc,
  videoCount,
  coursePercentage,
  seriesName,
  title,
  genres = [],
  instructors,
  courseType,
  level,
  highlighted,
  isCourseStarted,
  isSmallCard,
  isLocked,
  isSlider,
  colorScheme,
  upComingEvent,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}: ICourseData & {
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
}) => {
  return (
    <>
      <Card
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className={`${isSlider ? 'course-card group overflow-hidden w-full' : 'w-full'} ${isSmallCard ? 'h-[275px] md:h-[300px]' : 'h-[334px] md:h-[352px]'} relative`}
        sx={{
          boxShadow: 'none',
          backgroundColor: cardBackgroundColor || colorScheme?.card.bg,
          color: colorScheme?.card.text,
        }}
      >
        {isLocked && (
          <div
            className="absolute z-10 flex flex-col items-center justify-center w-full h-full pointer-events-none inset"
            style={{
              backgroundColor: !highlighted
                ? 'rgba(84,86,104,0.7)'
                : alpha(colorScheme?.card.bg || '#ffffff', 0.7),
              backdropFilter: 'blur(2px)',
            }}
          >
            <div className="-translate-y-20 text-center">
              <LockRounded className="h-12" />
              <Typography variant="s1" component="p">
                Abbonati per guardare
              </Typography>
            </div>
          </div>
        )}
        <CardActionArea className="flex flex-col justify-start w-full h-full rounded">
          <div className="relative w-full">
            <CardMedia
              component="img"
              image={imageSrc || '/assets/images/card-placeholder.png'}
              alt={title}
              className="object-cover relative w-full h-[180px] md:h-[196px]"
            />
            {!isCourseStarted && courseType && (
              <Chip
                label={
                  <Typography variant="button-s" className="capitalize">
                    {adaptCourseTypeText(courseType)}
                  </Typography>
                }
                size="small"
                variant="filled"
                className="absolute top-4 left-4 min-h-[24px]"
                sx={{
                  backgroundColor: colorScheme?.courseTypeTag.bg,
                  color: colorScheme?.courseTypeTag.text,
                }}
              />
            )}
            {!!upComingEvent && (
              <Chip
                avatar={
                  <MUIAvatar src="assets/images/catalog/calendar.png" className="mr-0 w-6 h-6" />
                }
                label={
                  <Typography variant="button-s" component="p">
                    {upComingEvent}
                  </Typography>
                }
                size="medium"
                className="absolute top-4 right-4 bg-neutral-main"
                sx={{ padding: '0', height: '30px' }}
              />
            )}
            {!isCourseStarted && videoCount !== undefined && (
              <Chip
                label={
                  <Typography variant="button-s">
                    {`${videoCount} lezion${videoCount !== 1 ? 'i' : 'e'}`}
                  </Typography>
                }
                size="medium"
                className="absolute top-4 right-4 bg-neutral-main"
                avatar={<MUIAvatar src="/assets/images/play-arrow-filled.png" className="mr-0" />}
              />
            )}
            {isCourseStarted && coursePercentage !== undefined && !seriesName && (
              <Chip
                label={`${coursePercentage}%`}
                size="medium"
                className="absolute top-4 right-4 bg-neutral-main"
                sx={{ padding: '0', height: '30px' }}
              />
            )}
          </div>
          <CardContent
            className={`w-full flex flex-col flex-1 justify-between pb-4`}
            style={{
              color: colorScheme?.card.text,
            }}
          >
            <div className="space-y-2 w-full flex flex-col items-start">
              <div className="flex items-center gap-4 w-full">
                <Typography
                  variant="h5"
                  color={colorScheme?.card.text}
                  className="gap-2.5 self-stretch w-full my-auto text-left line-clamp-2"
                >
                  {title}
                </Typography>
                {isCourseStarted && !seriesName && (
                  <IconButton
                    size="medium"
                    color="primary"
                    className="button-icon-contained button-icon-primary h-[44px] px-2"
                    sx={{ width: '44px !important' }}
                  >
                    <PlayArrowRounded />
                  </IconButton>
                )}
              </div>

              {!isCourseStarted && (
                <div className="flex flex-wrap gap-1">
                  {
                    <DifficultyLevelChip
                      courseType={courseType}
                      level={level as SkillLevel['name']}
                      highlighted={highlighted}
                    />
                  }
                  {Array.isArray(genres) &&
                    genres.map((genre, index) => (
                      <Chip
                        key={index}
                        label={
                          <Typography
                            variant="button-s"
                            sx={{ color: colorScheme?.musicalGenre.text }}
                          >
                            {genre}
                          </Typography>
                        }
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{
                          borderColor: colorScheme?.musicalGenre.border,
                        }}
                      />
                    ))}
                </div>
              )}
              {seriesName ? (
                <Chip
                  label={
                    <Typography variant="button-s" className="capitalize">
                      {seriesName}
                    </Typography>
                  }
                  size="small"
                  variant="filled"
                  className="w-auto"
                  sx={{
                    backgroundColor: colorScheme?.courseTypeTag.bg,
                    color: colorScheme?.courseTypeTag.text,
                  }}
                />
              ) : null}
            </div>

            {isCourseStarted ? (
              <div>
                <LinearProgress
                  className="mt-2 h-[8px] bg-neutral-main"
                  variant="determinate"
                  value={coursePercentage || 0}
                  sx={{ backgroundColor: '#E2E3E9' }}
                />
              </div>
            ) : (
              <>
                <div className="flex items-center mt-2 md:mt-4">
                  <Avatar
                    instructors={instructors}
                    courseType={courseType}
                    borderColor={colorScheme?.card.bg}
                  />
                </div>
              </>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </>
  );
};
